// apps/site-dna-web/src/components/ev/VehicleCard.js
/**
 * VehicleCard - Displays and edits a vehicle group in the fleet
 *
 * Shows vehicle type, quantity, schedule, and specs.
 * Follows the card pattern from GeneratorConfiguration.
 */

import React, { useState } from 'react';
import {
    Box,
    Typography,
    IconButton,
    TextField,
    Autocomplete,
    Collapse,
    Button,
    Switch,
    FormControlLabel,
    Checkbox,
    Divider,
    alpha,
} from '@mui/material';
import {
    Delete as DeleteIcon,
    ExpandMore as ExpandIcon,
    ExpandLess as CollapseIcon,
} from '@mui/icons-material';
import { useResponsive, vortxColors, NumberInput, FormRow } from '@vortx/shared-ui';

/**
 * VehicleCard Component
 *
 * @param {Object} props
 * @param {number} props.index - Position in fleet array
 * @param {Object} props.vehicle - Vehicle group data
 * @param {Array} props.vehicleDatabase - Available vehicles from API
 * @param {Function} props.onChange - Callback with updated vehicle
 * @param {Function} props.onRemove - Callback to remove this vehicle
 */
function VehicleCard({
    index,
    vehicle,
    vehicleDatabase = [],
    onChange,
    onRemove,
}) {
    const { isMobile } = useResponsive();
    const [showAdvanced, setShowAdvanced] = useState(false);

    // Find the selected vehicle from database
    const selectedVehicle = vehicleDatabase.find(v => v.id === vehicle.vehicle_id) || null;

    const handleFieldChange = (field, value) => {
        const updated = { ...vehicle, [field]: value };

        // Auto-calculate energy when daily_miles or efficiency changes
        if (field === 'daily_miles' || field === 'efficiency_kwh_per_mile') {
            const miles = field === 'daily_miles' ? value : (vehicle.daily_miles || 0);
            const eff = field === 'efficiency_kwh_per_mile' ? value : (vehicle.efficiency_kwh_per_mile || 0);
            updated.energy_kwh = Math.round(miles * eff * 10) / 10;
        }

        onChange(updated);
    };

    const handleVehicleSelect = (event, newValue) => {
        if (newValue) {
            const miles = vehicle.daily_miles || 100;
            const eff = newValue.efficiency_kwh_per_mile || 0;
            // Copy specs from database vehicle and auto-calculate energy
            onChange({
                ...vehicle,
                vehicle_id: newValue.id,
                vehicle_name: newValue.name,
                battery_capacity_kwh: newValue.battery_kwh,
                efficiency_kwh_per_mile: newValue.efficiency_kwh_per_mile,
                max_ac_charge_kw: newValue.max_ac_charge_kw,
                max_dc_charge_kw: newValue.max_dc_charge_kw,
                charging_curve: newValue.charging_curve || null,
                energy_kwh: Math.round(miles * eff * 10) / 10,
            });
        }
    };

    // Schedule mode: 'fixed' (same every day) or 'weekly'
    const scheduleMode = vehicle.schedule_mode || 'fixed';

    const handleScheduleModeToggle = () => {
        if (scheduleMode === 'fixed') {
            // Switching to weekly — pre-populate from flat fields
            const weeklySchedule = {};
            for (let d = 0; d < 7; d++) {
                weeklySchedule[String(d)] = {
                    departure_time: vehicle.departure_time || '06:00',
                    return_time: vehicle.return_time || '18:00',
                    daily_miles: vehicle.daily_miles || 100,
                };
            }
            onChange({ ...vehicle, schedule_mode: 'weekly', weekly_schedule: weeklySchedule });
        } else {
            // Switching to fixed — revert to flat fields, clear weekly
            onChange({ ...vehicle, schedule_mode: 'fixed', weekly_schedule: undefined });
        }
    };

    const handleWeeklyDayChange = (dayIndex, field, value) => {
        const ws = { ...(vehicle.weekly_schedule || {}) };
        const day = ws[String(dayIndex)];
        if (day === null || day === undefined) return; // day is "not present"
        ws[String(dayIndex)] = { ...day, [field]: value };
        onChange({ ...vehicle, weekly_schedule: ws });
    };

    const handleDayPresentToggle = (dayIndex) => {
        const ws = { ...(vehicle.weekly_schedule || {}) };
        if (ws[String(dayIndex)] === null) {
            // Re-enable with defaults from flat fields
            ws[String(dayIndex)] = {
                departure_time: vehicle.departure_time || '06:00',
                return_time: vehicle.return_time || '18:00',
                daily_miles: vehicle.daily_miles || 100,
            };
        } else {
            ws[String(dayIndex)] = null;
        }
        onChange({ ...vehicle, weekly_schedule: ws });
    };

    const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    return (
        <Box
            sx={{
                p: 2,
                mb: 2,
                bgcolor: alpha(vortxColors.gold, 0.05),
                borderRadius: 1,
                border: `1px solid ${alpha(vortxColors.gold, 0.2)}`,
            }}
        >
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <TextField
                    value={vehicle.name || vehicle.route_name || ''}
                    onChange={(e) => handleFieldChange('name', e.target.value)}
                    placeholder={vehicle.source === 'fleet_operations_import'
                        ? `Imported ${index + 1}`
                        : `Vehicle Group ${index + 1}`
                    }
                    variant="standard"
                    size="small"
                    InputProps={{
                        sx: { fontWeight: 600, fontSize: '1rem' },
                    }}
                    sx={{ flex: 1, mr: 1 }}
                />
                <IconButton
                    size="small"
                    onClick={onRemove}
                    sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                >
                    <DeleteIcon fontSize="small" />
                </IconButton>
            </Box>

            {/* Vehicle Selection */}
            <Autocomplete
                value={selectedVehicle}
                onChange={handleVehicleSelect}
                options={vehicleDatabase}
                groupBy={(option) => option.category}
                getOptionLabel={(option) => option.name}
                renderOption={(props, option) => (
                    <Box component="li" {...props}>
                        <Box>
                            <Typography variant="body2">{option.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                                {option.battery_kwh} kWh • {option.efficiency_kwh_per_mile} kWh/mi
                            </Typography>
                        </Box>
                    </Box>
                )}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Vehicle Type"
                        size="small"
                        sx={{ mb: 2 }}
                    />
                )}
                sx={{ mb: 2 }}
            />

            {/* Basic Fields */}
            <FormRow spacing={2}>
                <NumberInput
                    label="Quantity"
                    value={vehicle.quantity || 1}
                    onChange={(val) => handleFieldChange('quantity', val)}
                    min={1}
                    max={1000}
                    decimals={0}
                    sx={{ width: 120 }}
                />
                <NumberInput
                    label="Daily Miles"
                    value={vehicle.daily_miles || 100}
                    onChange={(val) => handleFieldChange('daily_miles', val)}
                    unit="mi"
                    min={0}
                    max={500}
                    decimals={0}
                    sx={{ flex: 1, minWidth: 120 }}
                />
                <NumberInput
                    label="Energy Needed"
                    value={vehicle.energy_kwh || Math.round((vehicle.daily_miles || 100) * (vehicle.efficiency_kwh_per_mile || 0.5) * 10) / 10}
                    onChange={(val) => handleFieldChange('energy_kwh', val)}
                    unit="kWh"
                    min={0}
                    max={10000}
                    decimals={1}
                    sx={{ flex: 1, minWidth: 120 }}
                />
            </FormRow>

            {/* Schedule Mode Toggle */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, mt: 1 }}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={scheduleMode === 'weekly'}
                            onChange={handleScheduleModeToggle}
                            size="small"
                        />
                    }
                    label={
                        <Typography variant="body2" color="text.secondary">
                            {scheduleMode === 'weekly' ? 'Weekly schedule' : 'Same every day'}
                        </Typography>
                    }
                />
            </Box>

            {/* Fixed Schedule (same every day) */}
            {scheduleMode === 'fixed' && (
                <FormRow spacing={2} sx={{ flexDirection: isMobile ? 'column' : 'row' }}>
                    <TextField
                        label="Departure Time"
                        type="time"
                        value={vehicle.departure_time || '06:00'}
                        onChange={(e) => handleFieldChange('departure_time', e.target.value)}
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        sx={{ flex: 1, minWidth: 120, width: isMobile ? '100%' : 'auto' }}
                    />
                    <TextField
                        label="Return Time"
                        type="time"
                        value={vehicle.return_time || '18:00'}
                        onChange={(e) => handleFieldChange('return_time', e.target.value)}
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        sx={{ flex: 1, minWidth: 120, width: isMobile ? '100%' : 'auto' }}
                    />
                </FormRow>
            )}

            {/* Weekly Schedule Grid */}
            {scheduleMode === 'weekly' && (
                <Box sx={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: 1,
                    overflowX: isMobile ? 'visible' : 'auto',
                    pb: 1,
                }}>
                    {DAY_LABELS.map((label, idx) => {
                        const dayData = (vehicle.weekly_schedule || {})[String(idx)];
                        const isPresent = dayData !== null && dayData !== undefined;

                        return (
                            <Box
                                key={idx}
                                sx={{
                                    flex: isMobile ? 'none' : '1 1 0',
                                    minWidth: isMobile ? '100%' : 100,
                                    p: 1,
                                    borderRadius: 1,
                                    bgcolor: isPresent
                                        ? alpha(vortxColors.teal, 0.05)
                                        : alpha(vortxColors.stone || '#999', 0.05),
                                    border: `1px solid ${alpha(isPresent ? vortxColors.teal : '#999', 0.2)}`,
                                    opacity: isPresent ? 1 : 0.5,
                                }}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                        {label}
                                    </Typography>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={!isPresent}
                                                onChange={() => handleDayPresentToggle(idx)}
                                                size="small"
                                                sx={{ p: 0.25 }}
                                            />
                                        }
                                        label={<Typography variant="caption" color="text.secondary">Off</Typography>}
                                        sx={{ m: 0 }}
                                    />
                                </Box>
                                {isPresent && (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                        <TextField
                                            type="time"
                                            label="Depart"
                                            value={dayData.departure_time || '06:00'}
                                            onChange={(e) => handleWeeklyDayChange(idx, 'departure_time', e.target.value)}
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                            fullWidth
                                        />
                                        <TextField
                                            type="time"
                                            label="Return"
                                            value={dayData.return_time || '18:00'}
                                            onChange={(e) => handleWeeklyDayChange(idx, 'return_time', e.target.value)}
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                            fullWidth
                                        />
                                        <NumberInput
                                            label="Miles"
                                            value={dayData.daily_miles || 100}
                                            onChange={(val) => handleWeeklyDayChange(idx, 'daily_miles', val)}
                                            min={0}
                                            max={500}
                                            decimals={0}
                                            size="small"
                                        />
                                    </Box>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            )}

            {/* Advanced Toggle */}
            <Button
                size="small"
                onClick={() => setShowAdvanced(!showAdvanced)}
                endIcon={showAdvanced ? <CollapseIcon /> : <ExpandIcon />}
                sx={{ color: 'text.secondary', mt: 1 }}
            >
                {showAdvanced ? 'Hide' : 'Show'} Specs
            </Button>

            {/* Advanced Specs (Collapsible) */}
            <Collapse in={showAdvanced}>
                <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${alpha(vortxColors.gold, 0.1)}` }}>
                    <FormRow spacing={2}>
                        <NumberInput
                            label="Battery Capacity"
                            value={vehicle.battery_capacity_kwh || 100}
                            onChange={(val) => handleFieldChange('battery_capacity_kwh', val)}
                            unit="kWh"
                            min={10}
                            max={2000}
                            decimals={0}
                            sx={{ flex: 1, minWidth: 140 }}
                        />
                        <NumberInput
                            label="Efficiency"
                            value={vehicle.efficiency_kwh_per_mile || 0.5}
                            onChange={(val) => handleFieldChange('efficiency_kwh_per_mile', val)}
                            unit="kWh/mi"
                            min={0.1}
                            max={5}
                            decimals={2}
                            sx={{ flex: 1, minWidth: 140 }}
                        />
                    </FormRow>
                    <FormRow spacing={2}>
                        <NumberInput
                            label="Max AC Charge"
                            value={vehicle.max_ac_charge_kw || 19.2}
                            onChange={(val) => handleFieldChange('max_ac_charge_kw', val)}
                            unit="kW"
                            min={0}
                            max={50}
                            decimals={1}
                            sx={{ flex: 1, minWidth: 140 }}
                        />
                        <NumberInput
                            label="Max DC Charge"
                            value={vehicle.max_dc_charge_kw || 150}
                            onChange={(val) => handleFieldChange('max_dc_charge_kw', val)}
                            unit="kW"
                            min={0}
                            max={1000}
                            decimals={0}
                            sx={{ flex: 1, minWidth: 140 }}
                        />
                    </FormRow>
                </Box>
            </Collapse>
        </Box>
    );
}

export default VehicleCard;
